import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';

function fmtDate(s) { return s ? new Date(s.replace(' ', 'T') + 'Z').toLocaleString('ko-KR') : '-'; }

export default function CowriteList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');

  const load = () => { api.listDocs().then(setDocs); };
  useEffect(() => { load(); }, []);

  const submitCreate = async (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) { setCreateErr('제목을 입력하세요.'); return; }
    setCreating(true); setCreateErr('');
    try {
      const r = await api.createDoc({ title: newTitle.trim(), content_html: '' });
      setShowCreate(false);
      setNewTitle('');
      nav('/cowrite/' + r.id);
    } catch (e) { setCreateErr(e.message); }
    finally { setCreating(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`'${d.title}' 문서를 삭제하시겠습니까? (모든 버전 기록 포함)`)) return;
    try { await api.deleteDoc(d.id); load(); } catch (e) { alert(e.message); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-medical-800">공동작성</h1>
          <p className="text-sm text-medical-500">모든 회원이 함께 편집할 수 있으며, 저장 시 자동으로 버전이 기록됩니다. 이전 버전으로 롤백 가능합니다.</p>
        </div>
        <button onClick={() => { setShowCreate(true); setNewTitle(''); setCreateErr(''); }}
                className="bg-medical-600 hover:bg-medical-700 text-white text-sm font-semibold px-3 py-2 rounded-md">
          + 새 문서
        </button>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-medical-900/30 flex items-center justify-center z-40 px-4"
             onClick={() => !creating && setShowCreate(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitCreate}
                className="bg-white rounded-2xl shadow-card border border-medical-100 w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-medical-800 mb-1">새 공동작성 문서</h2>
            <p className="text-xs text-medical-500 mb-4">문서를 만든 뒤 편집기에서 내용을 작성하세요. 저장할 때마다 버전이 기록됩니다.</p>
            <label className="text-sm font-semibold text-medical-700">제목</label>
            <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                   placeholder="예) 2026년 ADC 연구 회의록"
                   className="w-full mt-1 mb-3 px-3 py-2 border border-medical-200 rounded-md focus:outline-none focus:ring-2 focus:ring-medical-300" />
            {createErr && <div className="text-sm text-red-600 mb-2">{createErr}</div>}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" disabled={creating} onClick={() => setShowCreate(false)}
                      className="px-4 py-2 rounded-md border border-medical-200 text-medical-700 hover:bg-medical-50">
                취소
              </button>
              <button type="submit" disabled={creating}
                      className="px-4 py-2 rounded-md bg-medical-600 hover:bg-medical-700 text-white font-semibold disabled:opacity-60">
                {creating ? '생성 중...' : '문서 생성 후 편집'}
              </button>
            </div>
          </form>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-card border border-medical-100 overflow-x-auto">
        <table className="w-full text-sm table-fixed min-w-[820px]">
          <colgroup>
            <col />
            <col className="w-20" />
            <col className="w-48" />
            <col className="w-36" />
            <col className="w-16" />
          </colgroup>
          <thead className="bg-medical-50 text-medical-700">
            <tr>
              <th className="text-left px-3 py-2">제목</th>
              <th className="text-left px-3 py-2">버전</th>
              <th className="text-left px-3 py-2">최근 편집자</th>
              <th className="text-left px-3 py-2">최근 편집일</th>
              <th className="text-left px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} className="border-t border-medical-50 hover:bg-medical-50/40 align-top">
                <td className="px-3 py-2">
                  <Link to={'/cowrite/' + d.id} className="font-medium text-medical-800 hover:underline break-all">{d.title}</Link>
                  <div className="text-[11px] text-medical-400 break-keep">작성자: {d.author_name} · {d.author_institution}</div>
                </td>
                <td className="px-3 py-2 text-medical-700">v{d.latest_version}</td>
                <td className="px-3 py-2 break-keep">
                  <div className="text-medical-800">{d.last_editor_name || '-'}</div>
                  <div className="text-[11px] text-medical-500">{d.last_editor_institution || ''}</div>
                </td>
                <td className="px-3 py-2 text-medical-700 text-xs leading-tight break-keep">{fmtDate(d.last_edited_at)}</td>
                <td className="px-3 py-2 text-right">
                  {d.created_by === user.id && (
                    <button onClick={() => remove(d)} className="text-red-500 text-xs hover:underline">삭제</button>
                  )}
                </td>
              </tr>
            ))}
            {docs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-medical-400">문서가 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
